import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, ArrowLeft, Loader2, ShieldCheck, PartyPopper } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full rounded-xl bg-cream border-2 border-ink px-4 py-3 text-sm font-medium placeholder:text-lilac focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow";
const labelCls = "block font-head font-bold text-xs text-plum mb-2";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

function FieldInput({ field, value, onChange }) {
  if (field.type === "select") {
    return (
      <select data-testid={`form-field-${field.id}`} value={value} onChange={onChange} className={`${inputCls} appearance-none ${value ? "" : "text-lilac"}`}>
        <option value="" disabled>Choose…</option>
        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === "textarea") {
    return <textarea data-testid={`form-field-${field.id}`} value={value} onChange={onChange} rows={4} placeholder={field.placeholder || ""} className={`${inputCls} resize-none`} />;
  }
  return <input data-testid={`form-field-${field.id}`} type={field.type || "text"} value={value} onChange={onChange} placeholder={field.placeholder || ""} className={inputCls} />;
}

export default function OrderPopup({ open, onClose, prefill, content, formConfig }) {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const steps = useMemo(
    () => (formConfig?.steps || []).map((s) => ({ ...s, fields: (s.fields || []).filter((f) => f.visible !== false) })),
    [formConfig]
  );

  useEffect(() => {
    if (open) {
      setValues({ ...prefill });
      setStep(0);
      setDone(null);
    }
  }, [open, prefill]);

  const price = content?.price ?? 2999;
  const original = content?.original_price ?? 5999;

  const valid = useMemo(() => {
    const shown = (f) => !f.show_if || values[f.show_if.field] === f.show_if.equals;
    const fields = (steps[step]?.fields || []).filter(shown);
    return fields.every((f) => {
      if (!f.required) return true;
      const v = (values[f.id] || "").trim();
      if (!v) return false;
      if (f.type === "email") return /\S+@\S+\.\S+/.test(v);
      return true;
    });
  }, [step, steps, values]);

  const set = (id) => (e) => setValues((v) => ({ ...v, [id]: e.target.value }));

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, { details: values, origin: window.location.origin });
      if (!data.checkout_url) {
        setLoading(false);
        toast.warning(data.warning || "Order saved! Payment gateway isn't configured yet — we'll contact you.");
        setDone({ orderId: data.order_id, orderRef: data.order_ref, paymentId: null });
        return;
      }
      window.location.assign(data.checkout_url);
    } catch (err) {
      setLoading(false);
      toast.error(err.response?.data?.detail || "Could not start checkout. Please try again.");
    }
  };

  const isLast = step === steps.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-ink/50 backdrop-blur-sm p-0 sm:p-6"
          data-testid="popup-order-modal"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-[2rem] border-[3px] border-ink bg-white shadow-[10px_10px_0_#2D1A32]"
          >
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b-[3px] border-ink px-6 sm:px-8 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="Vowyo" className="h-8 w-auto" />
                <div>
                  <p className="font-display font-semibold text-lg leading-none">Craft Their Song</p>
                  <p className="font-mono text-[10px] text-lilac mt-1 uppercase tracking-[0.2em]">
                    {done ? "Order confirmed" : `Step ${step + 1} of ${steps.length} · ${steps[step]?.title || ""}`}
                  </p>
                </div>
              </div>
              <button data-testid="order-popup-close-button" onClick={onClose} className="btn-pop w-9 h-9 rounded-full border-2 border-ink bg-cream flex items-center justify-center" aria-label="Close">
                <X size={16} />
              </button>
            </div>

            {done ? (
              <div className="px-6 sm:px-8 py-14 text-center" data-testid="order-success-screen">
                <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", bounce: 0.6 }}>
                  <span className="inline-flex w-20 h-20 rounded-full bg-mint border-[3px] border-ink shadow-[5px_5px_0_#2D1A32] items-center justify-center">
                    <PartyPopper size={36} />
                  </span>
                </motion.div>
                <h3 className="mt-6 font-display font-bold text-3xl">Your song is in the studio!</h3>
                <p className="mt-3 text-sm text-plum font-medium max-w-sm mx-auto">
                  {done.paymentId ? "Payment received." : "Order saved."} Our artists are reading your story about{" "}
                  <span className="text-pink font-bold">{values.recipient_name}</span> — the song will land at{" "}
                  <span className="text-pink font-bold">{values.buyer_email}</span>.
                </p>
                <div className="mt-8 inline-block rounded-2xl border-2 border-ink bg-cream px-6 py-4 text-left shadow-[4px_4px_0_#2D1A32]">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lilac">Order {done.orderRef || ""}</p>
                  <p className="font-mono text-xs text-ink mt-1" data-testid="order-success-id">{done.orderId}</p>
                  {done.paymentId && (
                    <>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lilac mt-3">Payment ID</p>
                      <p className="font-mono text-xs text-ink mt-1" data-testid="order-success-payment-id">{done.paymentId}</p>
                    </>
                  )}
                  <a href={`/track/${done.orderRef || done.orderId}`} data-testid="order-track-link"
                    className="mt-3 inline-block text-xs font-head font-bold text-cobalt underline underline-offset-2">
                    Track your order →
                  </a>
                </div>
                <div>
                  <button data-testid="order-success-close-button" onClick={onClose} className="btn-pop mt-8 rounded-full bg-pink text-white font-head font-bold px-8 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#2D1A32]">
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-6 sm:px-8 py-8">
                <div className="flex gap-2 mb-8">
                  {steps.map((_, i) => (
                    <div key={i} className={`h-2 flex-1 rounded-full border border-ink transition-colors duration-500 ${i <= step ? "bg-pink" : "bg-cream"}`} />
                  ))}
                </div>

                <div className="space-y-5" data-testid={`order-step-${step + 1}`}>
                  {(steps[step]?.fields || []).filter((f) => !f.show_if || values[f.show_if.field] === f.show_if.equals).map((f) => (
                    <div key={f.id}>
                      <label className={labelCls}>{f.label}{f.required ? " *" : ""}</label>
                      <FieldInput field={f} value={values[f.id] || ""} onChange={set(f.id)} />
                    </div>
                  ))}
                </div>

                {isLast && (
                  <div className="mt-6 rounded-2xl border-2 border-ink bg-sunny/30 p-5 flex items-center justify-between rotate-[-0.5deg]" data-testid="order-summary-card">
                    <div>
                      <p className="font-display font-semibold text-lg">A song for {values.recipient_name || "someone special"}</p>
                      <p className="text-xs text-plum font-medium mt-0.5">{values.occasion} · {values.genre} · {values.language}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xs text-lilac line-through">{inr(original)}</p>
                      <p className="font-display font-bold text-2xl text-pink">{inr(price)}</p>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex items-center justify-between gap-3">
                  {step > 0 ? (
                    <button data-testid="order-step-back-btn" onClick={() => setStep((s) => s - 1)} className="btn-pop flex items-center gap-2 rounded-full border-2 border-ink bg-cream px-5 py-3 text-sm font-head font-bold">
                      <ArrowLeft size={15} /> Back
                    </button>
                  ) : <span />}
                  {!isLast ? (
                    <button data-testid="order-step-next-btn" disabled={!valid} onClick={() => setStep((s) => s + 1)}
                      className="btn-pop rounded-full bg-ink text-white font-head font-bold px-8 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#FF3B7E] disabled:opacity-40 disabled:shadow-none">
                      Continue →
                    </button>
                  ) : (
                    <button data-testid="payment-pay-button" disabled={!valid || loading} onClick={pay}
                      className="btn-pop flex items-center gap-2 rounded-full bg-pink text-white font-head font-bold px-6 sm:px-8 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#2D1A32] disabled:opacity-40 disabled:shadow-none">
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                      {loading ? "Opening secure checkout…" : `Pay ${inr(price)} · Secure Checkout`}
                    </button>
                  )}
                </div>
                <p className="mt-4 text-center font-mono text-[10px] text-lilac uppercase tracking-[0.2em]">
                  One-time payment · Secure checkout · No subscription
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
